import type { Issue } from '../types/issue';
import { num, secondsToDays } from './format';
import { isDone, REFINED_PLUS, statusKey } from './status';
import { capTotalForRelease, type CapMap } from './capacity';
import type { ReleaseCal } from './releases';

// V1-Snapshots haben "metrics": einfache Counts. V2 erweitert das um
// Plan-/Kapa-Größen, damit der Burn-Up über die Zeit echte Aussagekraft
// bekommt. Beide Forms koexistieren in history.
export type SnapshotMetrics = {
  // Counts (V1 + V2)
  epics?: number;
  stories?: number;
  bugs?: number;
  closedStories?: number;
  closedBugs?: number;
  openStories?: number;
  openBugs?: number;
  byTeam?: Record<string, Record<string, number>>;

  // V2-Erweiterung in Personentagen
  roughTotal?: number;       // Σ Rough Estimation aller Epics
  refinedStoriesPT?: number; // Σ SP/Est der refined Stories
  refinedBugsPT?: number;    // Σ SP/Est der refined Bugs
  capacityTotal?: number;    // Σ Kapa über alle Teams×Releases×Monate
};

export type Snapshot = {
  date: string;        // ISO
  fileName?: string;
  importedAt?: string;
  metrics: SnapshotMetrics;
};

// Berechnet die V2-erweiterte SnapshotMetrik aus dem aktuellen Stand.
// Wird beim Import aufgerufen, sodass jeder neue Snapshot diese reichere
// Form bekommt. Ältere V1-Snapshots in history bleiben unangetastet.
export function computeSnapshotMetrics(
  rows: Issue[],
  capacityByTeam: CapMap,
  releaseCal: Record<string, ReleaseCal>,
  spToPtFactor: number,
): SnapshotMetrics {
  let epics = 0, stories = 0, bugs = 0;
  let closedStories = 0, closedBugs = 0, openStories = 0, openBugs = 0;
  let roughTotal = 0, refinedStoriesPT = 0, refinedBugsPT = 0;
  const byTeam: Record<string, Record<string, number>> = {};

  for (const r of rows) {
    const t  = r['Issue Type'];
    const s  = r['Status'] ?? '';
    const team = (r['Team Name'] as string) || '—';
    const teamCounts = (byTeam[team] ??= { epics: 0, stories: 0, bugs: 0, closed: 0, open: 0 });

    if (t === 'Epic') {
      epics++;
      teamCounts.epics = (teamCounts.epics ?? 0) + 1;
      const rough = num(r['Custom field (Rough Estimation (d))']) ?? 0;
      if (rough > 0) roughTotal += rough;
      continue;
    }
    if (t !== 'Story' && t !== 'Bug') continue;

    const done = isDone(s);
    if (t === 'Story') {
      stories++;
      if (done) { closedStories++; teamCounts.closed = (teamCounts.closed ?? 0) + 1; }
      else      { openStories++;   teamCounts.open   = (teamCounts.open   ?? 0) + 1; }
      teamCounts.stories = (teamCounts.stories ?? 0) + 1;
    } else {
      bugs++;
      if (done) { closedBugs++;    teamCounts.closed = (teamCounts.closed ?? 0) + 1; }
      else      { openBugs++;      teamCounts.open   = (teamCounts.open   ?? 0) + 1; }
      teamCounts.bugs = (teamCounts.bugs ?? 0) + 1;
    }

    // Refined-PT: Items in REFINED_PLUS-States mit gepflegtem SP/Est
    if (REFINED_PLUS.has(statusKey(s))) {
      const sp  = num(r['Custom field (Story Points)']);
      const est = secondsToDays(r['Σ Original Estimate']);
      const v = (sp != null && sp > 0) ? sp * spToPtFactor : (est != null && est > 0 ? est : 0);
      if (t === 'Story') refinedStoriesPT += v;
      else                refinedBugsPT    += v;
    }
  }

  // Kapazität: Σ über alle Releases (aus dem Kalender), alle Teams
  let capacityTotal = 0;
  for (const rel of Object.keys(releaseCal)) {
    capacityTotal += capTotalForRelease(capacityByTeam, rel);
  }

  return {
    epics, stories, bugs,
    closedStories, closedBugs, openStories, openBugs,
    byTeam,
    roughTotal,
    refinedStoriesPT,
    refinedBugsPT,
    capacityTotal,
  };
}

// History-Helper: an die history-Liste einen neuen Snapshot anhängen.
// Wenn der vorhandene letzte Snapshot vom gleichen Tag stammt, wird er
// ersetzt — sonst legen wir einen neuen Eintrag an. So bleibt die
// History pro Tag stabil bei mehrfachen Imports.
export function appendSnapshot(history: Snapshot[], snap: Snapshot): Snapshot[] {
  const out = [...history];
  const day = snap.date.slice(0, 10);
  const last = out[out.length - 1];
  if (last && last.date.slice(0, 10) === day) {
    out[out.length - 1] = snap;
  } else {
    out.push(snap);
  }
  return out;
}
